import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {EventsService} from '@services/events.service';
import {debounceTime, filter, map, takeUntil} from 'rxjs/operators';
import {animate, animateChild, group, query, state, style, transition, trigger} from '@angular/animations';
import {Subject} from 'rxjs';
import {timer} from 'rxjs';
import {NotyPosition} from './noty.enums';

/**
 * Всплывающее уведомление
 */
@Component({
    selector: 'app-noty',
    template: `
        <div [@slideIn]="animationState"
             *ngIf="!hidden" class="noty noty--{{config.type}} noty--{{config.position}}">
            <div class="noty__content-container">
                <div class="noty__close" (click)="hide()"><lib-icon type="cross" [size]="10"></lib-icon></div>
                <div class="noty__content">
                    <div *ngIf="config.title"><b>{{config.title}}</b></div>
                    <div [innerHTML]="config.content"></div>
                </div>
            </div>
            <div *ngIf="config.controls?.length" class="noty__controls">
                <ng-container *ngFor="let control of config.controls">
                    <a *ngIf="control.href" [href]="control.href" class="button-transparent" target="_blank">{{control.title}}</a>
                    <button *ngIf="control.callback" type="button" class="button-transparent" (click)="control.callback()">
                        {{control.title}}
                    </button>
                </ng-container>
            </div>
            <div class="noty__countdown" *ngIf="config.timeout" [@countdown]="{value: 'end', params: {timeout: config.timeout}}"></div>
        </div>
    `,
    styleUrls: ['./noty.component.scss'],
    animations: [
        trigger('slideIn', [
            state('opened', style({ opacity: 1 })),
            state('bottom-center', style({ opacity: 0, bottom: -200 })),
            state('top-right', style({ opacity: 0, right: -400 })),
            transition('* => opened', [
                /**
                 * Чтобы одна анимация при вложенности не блокировалась другой, нужно ставить их в очередь
                 * и запускать дочернюю через animateChild() после запуска родительской
                 * https://github.com/angular/angular/issues/20823#issuecomment-369049930
                 */
                group([
                    animate('0.25s ease-out'),
                    query('@countdown', [animateChild()], { optional: true })
                ]),
            ]),
        ]),
        trigger('countdown', [
            state('end', style({
                width: 0
            })),
            transition('*=>end', [animate('{{timeout}}ms')], {params : {timeout: 0}}),
        ])
    ]
})

export class NotyComponent implements OnInit {

    public hidden = true;

    private defaultConfig: NotyConfig = {
        id: '',
        content: '',
        type: 'info',
        position: NotyPosition.topRight,
        timeout: 10000,
        hideOnRouteChange: true,
    };

    private defaultSuccessConfig: NotyConfig = {
        id: '',
        content: '',
        type: 'success',
        position: NotyPosition.topRight,
        timeout: 5000,
        hideOnRouteChange: false,
    };

    public config: NotyConfig = this.defaultConfig;
    public animationState: 'opened' | NotyPosition | '' = '';

    /**
     * «Прерыватель таймеров», чтобы при показе нового уведомления мы забывали о неслучившемся таймере старого
     */
    private timerEnd$ = new Subject();

    constructor(
        public events: EventsService,
        private cd: ChangeDetectorRef,
    ) {
    }

    ngOnInit() {
        this.events.notyShow$.pipe(
            map((config: NotyConfig) => {
                this.hide();

                if (config.type === 'success') {
                    return Object.assign({}, this.defaultSuccessConfig, config);
                } else {
                    return Object.assign({}, this.defaultConfig, config);
                }
            }),
            /**
             * debounceTime чтобы сначала отработал Hide и только потом снова show
             */
            debounceTime(30),
        ).subscribe((config: NotyConfig) => {
            this.animationState = config.position;
            this.show(config);
            this.cd.detectChanges();
            this.animationState = 'opened';
            this.cd.detectChanges();

            /**
             * Ставим таймер из конфига, по окончании которого закроем уведомление
             */
            if (config.timeout) {
                timer(config.timeout).pipe(
                    takeUntil(this.timerEnd$)
                ).subscribe(() => this.hide());
            }
        });
        this.events.notyHide$.subscribe((id: number|string|null) => {
            if (id) {
                if (this.config.id === id) {
                    this.hide();
                }
            } else {
                this.hide();
            }
        });

        /**
         * По событию, если есть отображаемое уведомление с таймером - скрываем его
         */
        this.events.notyHideOnRouteChange$.pipe(
            filter(() => Boolean(!this.hidden && this.config.hideOnRouteChange)),
        ).subscribe(() => {
            this.hide();
        });

        this.events.logout$.subscribe(() => {
            this.hide();
        });
    }

    show(config: NotyConfig) {
        this.config = config;
        this.hidden = false;
    }

    hide() {
        this.timerEnd$.next();
        this.config = this.defaultConfig;
        this.hidden = true;
    }
}


export interface NotyConfig {
    content: string;
    id?: number|string;
    title?: string;
    type?: 'info' | 'warning' | 'error' | 'success';
    controls?: NotyControl[];
    position?: NotyPosition;
    hideOnRouteChange?: boolean;
    timeout?: number;           // Сколько в милисекундах ждать, чтобы автоматически скрыть уведомление
}

export interface NotyControl {
    title: string;
    href?: string;
    callback?: () => void;
}
